import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Search, RefreshCw, History, ChevronLeft, ChevronRight } from 'lucide-react';
import { fetchAuditLogs, fetchAuditLogFilterOptions } from './hooks/hooks';

const NAV = '#001f3f';
const TEAL = '#0d9488';
const PAGE_SIZE = 50;

const ActionBadge = ({ action }) => {
  const map = {
    create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    update: 'bg-amber-50 text-amber-700 border-amber-200',
    delete: 'bg-red-50 text-red-700 border-red-200',
    login: 'bg-blue-50 text-blue-700 border-blue-200',
    login_failed: 'bg-red-50 text-red-700 border-red-200',
  };
  const label = { login_failed: 'LOGIN FAILED' }[action] || action?.toUpperCase();
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${map[action] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {label}
    </span>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : '—');

export default function AuditLogs() {
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [appLabel, setAppLabel] = useState('');
  const [modelName, setModelName] = useState('');
  const [page, setPage] = useState(1);

  const { data: filterOptions } = useQuery({
    queryKey: ['audit-log-filter-options'],
    queryFn: fetchAuditLogFilterOptions,
  });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['audit-logs', search, action, appLabel, modelName, page],
    queryFn: () => fetchAuditLogs({
      search: search || undefined,
      action: action || undefined,
      app_label: appLabel || undefined,
      model_name: modelName || undefined,
      page,
      page_size: PAGE_SIZE,
    }),
    placeholderData: keepPreviousData,
  });

  const results = data?.results || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const modelsForApp = (filterOptions?.models || []).filter(
    (m) => !appLabel || m.app_label === appLabel
  );

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Audit Logs</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Every create, update, and delete recorded across the platform — who did what, and when
          </p>
        </div>
        <button onClick={refetch} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
          <RefreshCw size={13} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Stat */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 max-w-xs">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: NAV + '18' }}>
          <History size={22} style={{ color: NAV }} />
        </div>
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Entries</p>
          <p className="text-lg font-bold text-slate-800 leading-tight">{total.toLocaleString()}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => updateFilter(setSearch)(e.target.value)}
            placeholder="Search by object or user…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <select
          value={action}
          onChange={(e) => updateFilter(setAction)(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">All actions</option>
          <option value="create">Create</option>
          <option value="update">Update</option>
          <option value="delete">Delete</option>
          <option value="login">Login</option>
          <option value="login_failed">Login Failed</option>
        </select>

        <select
          value={appLabel}
          onChange={(e) => { updateFilter(setAppLabel)(e.target.value); setModelName(''); }}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">All modules</option>
          {(filterOptions?.app_labels || []).map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>

        <select
          value={modelName}
          onChange={(e) => updateFilter(setModelName)(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <option value="">All models</option>
          {modelsForApp.map((m) => (
            <option key={`${m.app_label}.${m.model_name}`} value={m.model_name}>{m.model_name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40 text-slate-400">Loading…</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {['Time', 'Action', 'Module', 'Object', 'User', 'IP'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {results.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(log.timestamp)}</td>
                  <td className="px-4 py-3"><ActionBadge action={log.action} /></td>
                  <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{log.app_label}.{log.model_name}</td>
                  <td className="px-4 py-3 text-slate-800 max-w-xs truncate">{log.object_repr || `#${log.object_id}`}</td>
                  <td className="px-4 py-3 text-xs">
                    {log.user ? (
                      <>
                        <div className="font-medium text-slate-700">{log.user_name || '—'}</div>
                        <div className="text-slate-400">{log.user_email}</div>
                      </>
                    ) : log.action === 'login_failed' ? (
                      <span className="text-slate-400">Unresolved (bad credentials)</span>
                    ) : (
                      <span className="text-slate-400">System</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{log.ip_address || '—'}</td>
                </tr>
              ))}
              {results.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No audit log entries found</td></tr>
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
